import { useState, useEffect, useRef } from 'react';
import { LogOut, ChevronDown, Clock } from 'lucide-react';
import type { User } from '@shared/types';

import type { UserMenuLabels } from './Layout.types';

const DEFAULT_USER_MENU_LABELS: UserMenuLabels = {
  extend: '연장',
  reLogin: '재로그인',
  logout: '로그아웃',
  logoutConfirm: '로그아웃 하시겠습니까?',
  myAccount: '내 계정',
  expired: '만료됨',
  orgPrefix: '조직:',
  positionPrefix: '직책:',
};

export interface UserMenuProps {
  user?: User | null;
  onLogout?: () => void;
  onExtendToken?: () => void;
  isMobile?: boolean;
  labels?: UserMenuLabels;
}

export function UserMenu({
  user,
  onLogout,
  onExtendToken,
  isMobile = false,
  labels: labelsProp,
}: UserMenuProps) {
  const labels = labelsProp ?? DEFAULT_USER_MENU_LABELS;
  const [isOpen, setIsOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!user?.tokenExpiresAt) return;

    const updateTime = () => {
      const now = Date.now();
      const remaining = user.tokenExpiresAt! - now;

      if (remaining <= 0) {
        setTimeRemaining(labels.expired);
        setIsExpired(true);
        return;
      }

      setIsExpired(false);
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [user?.tokenExpiresAt, labels.expired]);

  if (!user) return null;

  const getUserDisplayInfo = () => {
    if (user.userType === 'portal-admin') {
      return {
        displayName: user.displayName,
        subtitle: user.adminUserId,
        userId: user.adminUserId,
        email: undefined,
        organization: undefined,
        position: undefined,
      };
    }
    return {
      displayName: user.displayName,
      subtitle: user.userId,
      userId: user.userId,
      email: user.email,
      organization: user.tenantId,
      position: user.position,
    };
  };

  const userInfo = getUserDisplayInfo();

  const handleExtendClick = () => {
    if (isExpired) {
      onLogout?.();
    } else {
      onExtendToken?.();
    }
    setIsOpen(false);
  };

  const handleButtonClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(!isOpen);
  };

  // 모바일: 아바타만 표시, 클릭 시 드롭다운
  if (isMobile) {
    const handleMobileButtonClick = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
        });
      }
      setIsOpen(!isOpen);
    };

    return (
      <div style={{ position: 'relative', zIndex: 10 }}>
        <button
          ref={buttonRef}
          onClick={handleMobileButtonClick}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: 'hsl(221 83% 53%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 500,
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 10,
          }}
        >
          {userInfo.displayName?.charAt(0).toUpperCase() || 'U'}
        </button>

        {isOpen && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 60 }}
              onClick={() => setIsOpen(false)}
            />
            <div style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              width: '260px',
              backgroundColor: 'white',
              border: '1px solid hsl(220 13% 91%)',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              zIndex: 61,
              overflow: 'hidden',
            }}>
              {/* 사용자 정보 */}
              <div style={{ padding: '16px', borderBottom: '1px solid hsl(220 13% 91%)', backgroundColor: 'hsl(220 14% 98%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: 'hsl(221 83% 53%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 500,
                  }}>
                    {userInfo.displayName?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#000000' }}>{userInfo.displayName}</div>
                    <div style={{ fontSize: '13px', color: '#000000' }}>{userInfo.subtitle}</div>
                  </div>
                </div>
              </div>

              {/* 세션 정보 */}
              {user.tokenExpiresAt && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid hsl(220 13% 91%)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: isExpired ? 'hsl(0 84% 60%)' : '#000000' }}>
                      <Clock style={{ width: '14px', height: '14px' }} />
                      <span>{timeRemaining}</span>
                    </div>
                    {(onExtendToken || isExpired) && (
                      <button
                        onClick={handleExtendClick}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: isExpired ? 'none' : '1px solid hsl(220 13% 91%)',
                          backgroundColor: isExpired ? 'hsl(0 84% 60%)' : 'transparent',
                          color: isExpired ? 'white' : '#000000',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        {isExpired ? labels.reLogin : labels.extend}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* 로그아웃 */}
              <div style={{ padding: '8px' }}>
                <button
                  onClick={() => {
                    if (onLogout && confirm(labels.logoutConfirm)) {
                      onLogout();
                    }
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'hsl(0 84% 60%)',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(0 84% 60% / 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogOut style={{ width: '18px', height: '18px' }} />
                  {labels.logout}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // 데스크탑: 기존 UI
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {user.tokenExpiresAt && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '12px',
          color: isExpired ? 'hsl(0 84% 60%)' : '#000000'
        }}>
          <Clock style={{ width: '14px', height: '14px' }} />
          <span style={{ fontWeight: 500 }}>{timeRemaining}</span>
        </div>
      )}

      {(onExtendToken || isExpired) && (
        <button
          onClick={handleExtendClick}
          style={{
            padding: '2px 8px',
            fontSize: '12px',
            borderRadius: '4px',
            border: isExpired ? '1px solid hsl(0 84% 60%)' : '1px solid hsl(220 13% 91%)',
            backgroundColor: isExpired ? 'hsl(0 84% 60%)' : 'transparent',
            color: isExpired ? 'white' : '#000000',
            cursor: 'pointer',
          }}
        >
          {isExpired ? labels.reLogin : labels.extend}
        </button>
      )}

      <div style={{ position: 'relative', zIndex: 10 }}>
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
            position: 'relative',
            zIndex: 10,
            pointerEvents: 'auto',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(220 14% 96%)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'hsl(221 83% 53%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 500,
          }}>
            {userInfo.displayName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#000000' }}>{userInfo.displayName}</span>
            <span style={{ fontSize: '12px', color: '#000000' }}>{userInfo.subtitle}</span>
          </div>
          <ChevronDown style={{ width: '16px', height: '16px', color: 'hsl(220 9% 46%)' }} />
        </button>

        {isOpen && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 60 }}
              onClick={() => setIsOpen(false)}
            />
            <div style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              width: '224px',
              backgroundColor: 'white',
              border: '1px solid hsl(220 13% 91%)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 61,
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid hsl(220 13% 91%)' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>{labels.myAccount}</div>
              </div>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid hsl(220 13% 91%)' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#000000' }}>{userInfo.displayName}</div>
                {userInfo.userId && <div style={{ fontSize: '12px', color: '#000000', marginTop: '4px' }}>ID: {userInfo.userId}</div>}
                {userInfo.email && <div style={{ fontSize: '12px', color: '#000000', marginTop: '4px' }}>{userInfo.email}</div>}
                {userInfo.organization && <div style={{ fontSize: '12px', color: '#000000', marginTop: '4px' }}>{labels.orgPrefix} {userInfo.organization}</div>}
                {userInfo.position && <div style={{ fontSize: '12px', color: '#000000', marginTop: '4px' }}>{labels.positionPrefix} {userInfo.position}</div>}
              </div>
              <div style={{ padding: '8px' }}>
                <button
                  onClick={() => {
                    if (onLogout && confirm(labels.logoutConfirm)) {
                      onLogout();
                    }
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'hsl(0 84% 60%)',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(0 84% 60% / 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogOut style={{ width: '16px', height: '16px' }} />
                  {labels.logout}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
